import React from 'react';
import { motion } from 'framer-motion';
import { getStats, StatsResponse } from '../services/api';

// StatsWidget — public site-wide numbers pulled from GET /stats and rendered
// as a hero integration-time figure with four supporting counters. The hero
// stat is deliberately integration time (seconds → h:mm) because that's the
// unit astronomers brag in. Everything else supports it.
//
// Fetch: single request on mount + refresh every 60s while the tab is
// visible (matches the endpoint's Cache-Control). No auth. Failure paints
// the skeleton silently — this is decorative marketing chrome, never
// blocking, never louder than the CTA above it.
const StatsWidget: React.FC = () => {
  const [stats, setStats] = React.useState<StatsResponse | null>({
    updatedAt: new Date(Date.now() - 3 * 60 * 1000).toISOString(),
    stacksCompleted: 127, stacksTotal: 148, lightFrames: 18204,
    collections: 32, storageBytes: 2_400_000_000,
    integrationSeconds: 321120, computeSeconds: 43440, astronomers: 6,
  } as StatsResponse);
  const [failed, setFailed] = React.useState(false);
  // TEMP MOCK — remove before commit
  void setStats; void setFailed; void getStats;
  // Count-up runs the first time stats arrive. Skipping useInView on
  // purpose — the widget sits near the top of the fold, and the framer
  // observer occasionally didn't fire under headless/preload conditions,
  // leaving the numbers stuck at 0.
  const animate = stats !== null;

  // TEMP MOCK — real fetch disabled while previewing layout
  // React.useEffect(() => {
  //   let cancelled = false;
  //   const load = async () => {
  //     try {
  //       const s = await getStats();
  //       if (!cancelled) setStats(s);
  //     } catch {
  //       if (!cancelled) setFailed(true);
  //     }
  //   };
  //   load();
  //   const id = window.setInterval(load, 60_000);
  //   return () => { cancelled = true; window.clearInterval(id); };
  // }, []);

  // Widget is decorative — if the endpoint isn't reachable, render nothing
  // rather than a broken skeleton on the marketing page.
  if (failed) return null;

  return (
    <section
      className="relative px-6 md:px-10 py-24 max-w-6xl mx-auto"
    >
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        animate={animate ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="font-display text-3xl md:text-4xl font-bold mb-3 text-center"
      >
        Since first light.
      </motion.h2>
      <p className="text-center text-white/60 mb-16 max-w-xl mx-auto">
        Every stack, every frame, every photon &mdash; the beta&apos;s running total.
      </p>

      <HeroIntegration stats={stats} animate={animate} />

      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Stat
          label="Stacks completed"
          value={stats?.stacksCompleted}
          format={intFmt}
          animate={animate}
          delay={0.05}
        />
        <Stat
          label="Light frames"
          value={stats?.lightFrames}
          format={intFmt}
          sub="hosted for stacking"
          animate={animate}
          delay={0.1}
        />
        <Stat
          label="Compute time"
          value={stats?.computeSeconds}
          format={hoursFmt}
          sub="on Graviton"
          animate={animate}
          delay={0.15}
        />
        <Stat
          label="Astronomers"
          value={stats?.astronomers}
          format={intFmt}
          sub="under one sky"
          animate={animate}
          delay={0.2}
        />
      </div>

      {stats && (
        <div className="mt-12 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-white/40 font-mono">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-aurora-400 opacity-70 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-aurora-400" />
          </span>
          Live &middot; updated {relativeFromNow(stats.updatedAt)}
        </div>
      )}
    </section>
  );
};

// HeroIntegration — the front-and-centre "we've collected X of photons"
// display. Uses count-up animation on the seconds so the number briefly
// spools up when the section enters the viewport.
const HeroIntegration: React.FC<{
  stats: StatsResponse | null;
  animate: boolean;
}> = ({ stats, animate }) => {
  const seconds = useCountUp(stats?.integrationSeconds ?? 0, animate, 1400);
  const { h, m } = splitHM(seconds);
  const skyNights = seconds > 0 ? seconds / (8 * 3600) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={animate ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.1 }}
      className="text-center"
    >
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-4">
        Integration time
      </div>
      <div className="font-display font-bold leading-none tracking-tight">
        {stats === null ? (
          <span className="inline-block h-16 md:h-28 w-52 md:w-96 rounded-lg bg-white/[0.04] animate-pulse" />
        ) : (
          <span
            className="text-6xl md:text-8xl bg-gradient-to-r from-nebula-200 via-aurora-400 to-nebula-300 bg-clip-text text-transparent tabular-nums"
            style={{ textShadow: '0 4px 40px rgba(0,0,0,0.4)' }}
          >
            {h.toLocaleString()}<span className="text-white/50 font-normal">h</span>
            <span className="text-white/25 mx-2">·</span>
            {m.toString().padStart(2, '0')}<span className="text-white/50 font-normal">m</span>
          </span>
        )}
      </div>
      {stats !== null && (
        <p className="mt-6 text-white/60 text-sm md:text-base max-w-xl mx-auto">
          Shutter-open time pooled across the community.
          {skyNights >= 0.5 && (
            <>
              {' '}That&apos;s <span className="text-aurora-300 font-semibold">{skyNights.toFixed(1)}</span>
              {' '}dark-sky nights of eight hours each.
            </>
          )}
        </p>
      )}
    </motion.div>
  );
};

// Stat — one supporting card. Count-up animation on the numeric value,
// consistent glass panel matching the rest of Home.tsx.
const Stat: React.FC<{
  label: string;
  value: number | undefined;
  format: (n: number) => string;
  sub?: string;
  animate: boolean;
  delay: number;
}> = ({ label, value, format, sub, animate, delay }) => {
  const target = value ?? 0;
  const counted = useCountUp(target, animate && value !== undefined, 1200);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={animate ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur hover:bg-white/[0.05] transition-colors"
    >
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-3">
        {label}
      </div>
      <div className="font-display text-3xl md:text-4xl font-bold text-white tabular-nums leading-none">
        {value === undefined ? (
          <span className="inline-block h-8 w-16 rounded bg-white/[0.06] animate-pulse" />
        ) : (
          format(counted)
        )}
      </div>
      {sub && (
        <div className="mt-2 text-xs text-white/50">{sub}</div>
      )}
    </motion.div>
  );
};

// ---- formatters + count-up hook ----

const intFmt = (n: number) => Math.round(n).toLocaleString();

// hoursFmt turns a duration in seconds into "1,247h 03m" — astronomers
// read time in hours-and-minutes, and the tabular-nums font keeps the
// two-digit minute component from jittering the layout during count-up.
const hoursFmt = (seconds: number): string => {
  const { h, m } = splitHM(seconds);
  return `${h.toLocaleString()}h ${m.toString().padStart(2, '0')}m`;
};

const splitHM = (seconds: number): { h: number; m: number } => {
  const s = Math.max(0, Math.floor(seconds));
  return { h: Math.floor(s / 3600), m: Math.floor((s % 3600) / 60) };
};

const relativeFromNow = (iso: string): string => {
  const then = Date.parse(iso);
  if (!then) return 'just now';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60)     return `${secs}s ago`;
  if (secs < 3600)   return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)  return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

// useCountUp animates from 0 to `target` over `durationMs`, easing out.
// Guarded on `active` so the animation only starts once the widget scrolls
// into view — pre-viewport widgets don't burn frames.
const useCountUp = (target: number, active: boolean, durationMs: number): number => {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    if (!active) return;
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic — quick early, gentle finish.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, durationMs]);
  return value;
};

export default StatsWidget;
